import { Box, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import palette from 'src/theme/palette';

import { formatPercent } from './formatters';
import type { RetentionDistributionBucket } from './types';

interface DaysToSecondPurchaseChartProps {
  data: RetentionDistributionBucket[];
}

type BucketTooltipPayload = {
  payload?: RetentionDistributionBucket;
};

function SecondPurchaseBucketTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: BucketTooltipPayload[];
}) {
  if (!active || !payload?.[0]?.payload) return null;
  const row = payload[0].payload;
  const count = row.customers;
  const countLabel = count === 1 ? 'customer' : 'customers';

  return (
    <Box
      sx={{
        bgcolor: palette.common.white,
        border: `1px solid ${palette.grey[200]}`,
        borderRadius: 1,
        px: 1.25,
        py: 1,
        boxShadow: '0 8px 24px -12px rgba(15, 23, 42, 0.35)',
        maxWidth: 280,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          display: 'block',
          color: palette.text.primary,
          fontSize: '0.75rem',
          lineHeight: 1.35,
        }}
      >
        Second order after first: {row.bucket} days
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 0.65,
          color: palette.text.secondary,
          fontSize: '0.72rem',
          lineHeight: 1.45,
        }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 700, color: palette.text.primary }}
        >
          {count.toLocaleString()} {countLabel}
        </Box>
        {' · '}
        {formatPercent(row.percentage)} of analyzed customers
      </Typography>
    </Box>
  );
}

export function DaysToSecondPurchaseChart({
  data,
}: DaysToSecondPurchaseChartProps) {
  const activeColor = 'rgba(30, 237, 159, 0.88)';
  const mutedColor = 'rgba(30, 237, 159, 0.42)';

  return (
    <Box
      className="retention-pdf-chart-fit"
      sx={{
        width: '100%',
        minWidth: 0,
        // Altezza fissa solo sul plot: caption e tick X restano fuori dal clip di Recharts.
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        pb: 0.5,
      }}
    >
      <Box
        className="retention-pdf-chart-canvas"
        sx={{ width: '100%', height: 268, flexShrink: 0 }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, left: 4, bottom: 28 }}
          >
            <CartesianGrid vertical={false} stroke={palette.grey[200]} />
            <XAxis
              dataKey="bucket"
              interval={0}
              tick={{ fill: palette.text.secondary, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              height={36}
            />
            <YAxis
              tickFormatter={(value) => `${Math.round(Number(value) * 100)}%`}
              tick={{ fill: palette.text.secondary, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              width={44}
            />
            <Tooltip
              content={<SecondPurchaseBucketTooltip />}
              cursor={{ fill: 'rgba(15, 23, 42, 0.06)' }}
            />
            <Bar dataKey="percentage" radius={[8, 8, 0, 0]} maxBarSize={36}>
              {data.map((item) => (
                <Cell
                  key={item.bucket}
                  fill={item.customers > 0 ? activeColor : mutedColor}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Typography
        variant="caption"
        component="p"
        sx={{
          color: palette.text.secondary,
          mt: 1.25,
          mb: 0,
          px: 0.25,
          display: 'block',
          lineHeight: 1.45,
        }}
      >
        Share of analyzed customers by timing bucket — hover a bar for customer
        count and percentage.
      </Typography>
    </Box>
  );
}

export default DaysToSecondPurchaseChart;
